/**
 * Horse inventory screen (self-contained DOM overlay, like the villager trade
 * screen): the saddle slot, the armour slot and the horse's stats. Click a slot
 * with the matching item held to equip it, or click a filled slot to take the
 * item back.
 */

import { I } from '../game/itemIds';
import { HORSE_ARMOR, armorTierOf, type Horse } from '../entities/riding/horse';

export interface HorseScreenDeps {
  heldId(): number;
  consumeHeld(n: number): void;
  give(id: number): void;
  nameOf(id: number): string;
  iconFor(id: number): HTMLCanvasElement | null;
}

const K = 3;

export class HorseScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private horse: Horse | null = null;
  private onClose: (() => void) | null = null;

  constructor(parent: HTMLElement, private readonly d: HorseScreenDeps) {
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed', 'inset:0', 'display:none', 'align-items:center', 'justify-content:center',
      'z-index:60', 'font:13px/1.4 monospace', 'color:#3f3f3f',
      'background:linear-gradient(rgba(16,16,16,0.75),rgba(16,16,16,0.82))', 'user-select:none',
    ].join(';');
    const panel = document.createElement('div');
    // the real gui/container/horse.png frame (256x256 sheet), cropped to its top 176x72 (title, saddle + armour slots, horse window) at 3x
    panel.style.cssText = `position:relative;width:${176 * K}px;height:${72 * K}px;background:url(mc/gui/container/horse.png) 0 0/${256 * K}px ${256 * K}px no-repeat;image-rendering:pixelated;`;
    this.body = document.createElement('div');
    this.body.style.cssText = 'position:absolute;inset:0;';
    panel.appendChild(this.body);
    this.root.appendChild(panel);
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });
    window.addEventListener('keydown', (e) => { if (this.open && (e.code === 'Escape' || e.code === 'KeyE')) { e.preventDefault(); this.close(); } });
    parent.appendChild(this.root);
  }

  get open(): boolean { return this.root.style.display !== 'none'; }

  show(h: Horse, onClose: () => void): void {
    this.horse = h; this.onClose = onClose;
    try { document.exitPointerLock?.(); } catch { /* not locked */ }
    this.root.style.display = 'flex';
    this.render();
  }

  close(): void {
    if (!this.open) return;
    this.root.style.display = 'none';
    this.horse = null;
    const cb = this.onClose; this.onClose = null;
    cb?.();
  }

  /** A 16x16 vanilla slot hit area at GUI (x, y), showing the item icon when filled. */
  private slot(x: number, y: number, id: number, click: () => void): HTMLElement {
    const box = document.createElement('div');
    box.style.cssText = `position:absolute;left:${x * K}px;top:${y * K}px;width:${16 * K}px;height:${16 * K}px;cursor:pointer;display:flex;align-items:center;justify-content:center;`;
    const icon = id ? this.d.iconFor(id) : null;
    if (icon) { const c = icon.cloneNode(true) as HTMLCanvasElement; c.getContext('2d')?.drawImage(icon, 0, 0); c.style.cssText = `width:${16 * K}px;height:${16 * K}px;image-rendering:pixelated;`; box.appendChild(c); }
    box.title = id ? this.d.nameOf(id) : '';
    box.onclick = click;
    return box;
  }

  render(): void {
    const h = this.horse;
    if (!h) return;
    this.body.textContent = '';
    const title = document.createElement('div');
    title.style.cssText = `position:absolute;left:${8 * K}px;top:${6 * K}px;`;
    title.textContent = h.donkey ? 'Donkey' : 'Horse';
    this.body.appendChild(title);
    this.body.appendChild(this.slot(8, 18, h.saddled ? I.SADDLE : 0, () => {
      if (h.saddled) { h.saddled = false; this.d.give(I.SADDLE); } else if (this.d.heldId() === I.SADDLE) { h.saddled = true; this.d.consumeHeld(1); }
      this.render();
    }));
    if (!h.donkey) {
      this.body.appendChild(this.slot(8, 36, h.armorTier ? HORSE_ARMOR[h.armorTier] : 0, () => {
        const held = armorTierOf(this.d.heldId());
        if (h.armorTier) { this.d.give(HORSE_ARMOR[h.armorTier]); h.armorTier = 0; } else if (held > 0) { h.armorTier = held; this.d.consumeHeld(1); }
        this.render();
      }));
    }
    const st = document.createElement('div');
    st.style.cssText = `position:absolute;left:${82 * K}px;top:${20 * K}px;line-height:1.5;`;
    st.textContent = `Health ${Math.ceil(h.health)}/${Math.ceil(h.maxHealth)}\nSpeed ${(h.hSpeed * 43).toFixed(1)} b/s\nJump ${(h.jumpStrength * h.jumpStrength * 5.3).toFixed(1)}`;
    st.style.whiteSpace = 'pre';
    this.body.appendChild(st);
  }
}
